import React from 'react';
import classNames from 'classnames';
import ProgressBar from '@ifit/mp-progress-bar';
import styles from './index.module.scss';
import NativeClick, { METHODS } from '@ifit/mp-native-click';
import { II18n, withTranslation } from '@ifit/rapport-webview';
import rapportPackage from './rapport-web.json';
import { formatDay } from './date-format';
import LineWrap from '@ifit/mp-line-wrap';
import { withTheme } from '@ifit/themes/withTheme';

export interface BadgeData {
  achievementId: number;
  completed?: boolean;
  badgeImage: string;
  createdDate?: string; // optional in IMilestoneAchievement must be optional here
  completedDate?: string;
  description?: string;
  hasClaimedReward?: boolean;
  redemptionLink?: string | null;
  title?: string;
  challengeId?: number;
  completedWorkouts?: number;
  remainingWorkouts?: number;
  totalWorkouts?: number;
  milestoneCount?: number;
  milestoneYear?: number;
}

export interface IBadgeProps {
  data: BadgeData;
  i18n: II18n;
  referralSource: string;
  themeClass?: string;
  className?: string;
  verbose?: boolean;
  language?: string;
  imageOnly?: boolean;
}

const Badge = (props: IBadgeProps) => {
  const {
    themeClass,
    className,
    i18n,
    data,
    verbose,
    referralSource,
    language,
    imageOnly,
  } = props;

  const {
    achievementId,
    completedDate = '',
    badgeImage,
    title,
    totalWorkouts = 0,
    completedWorkouts = 0,
    completed,
    milestoneYear,
  } = data;

  const numberOfWorkoutsLabel = (
    currentWorkoutIndex: number,
    workoutTotal: number
  ) => {
    return i18n._translate(
      '%{currentWorkoutIndex} of %{workoutTotal} complete',
      {
        currentWorkoutIndex,
        workoutTotal,
      }
    );
  };

  const completionDate = new Date(completedDate);
  const formatDate = formatDay(completionDate, language);
  const totalProgress = (completedWorkouts / totalWorkouts) * 100;

  const disableMilestone = !completed && Boolean(milestoneYear);

  const currentYear: number = new Date().getFullYear();
  const isBadgeForPastYear: boolean =
    milestoneYear !== undefined && currentYear != milestoneYear;
  const disableNative = completed
    ? !completed && isBadgeForPastYear
    : isBadgeForPastYear;

  return (
    <div
      className={classNames(
        !imageOnly && styles['badge-container'],
        themeClass,
        className,
        { [styles.disabled]: disableMilestone }
      )}
      data-testid="mp-badge-container"
    >
      <NativeClick
        disableNative={disableNative}
        verbose={verbose}
        objectToSend={METHODS.achievementSelected(
          achievementId,
          referralSource
        )}
        data-testid="mp-badge-click"
      >
        <img src={badgeImage} alt="" className={classNames(styles['badge'])} />
        {!imageOnly && (
          <div className={styles['contain-title']}>
            <LineWrap className={styles.title} maxLines={2} text={title} />
          </div>
        )}
        {!completed && totalWorkouts && !imageOnly ? (
          <>
            <span className={styles.subtitle} data-testid="mp-badge-total">
              {numberOfWorkoutsLabel(completedWorkouts, totalWorkouts)}
            </span>
            <ProgressBar
              progress={totalProgress}
              height={8}
              className={styles.progressbar}
            />
          </>
        ) : (
          !disableMilestone &&
          !imageOnly && (
            <span data-testid="mp-badge-date" className={styles.subtitle}>
              {formatDate}
            </span>
          )
        )}
      </NativeClick>
    </div>
  );
};

export default withTheme(withTranslation(rapportPackage)(Badge));
